import React, { useEffect, useId, useRef, useState } from 'react'
import {
  ChevronDownIcon,
  DocumentTextIcon
} from '@heroicons/react/20/solid'
import type { OfimaConsecutivos } from '../types/order'

interface OfimaRemisionWithConsecutivosProps {
  idRemision?: string | number | null
  ofimaConsecutivos?: OfimaConsecutivos | null
  className?: string
  /** `compact`: celda de tabla (remisión + control discreto) */
  variant?: 'default' | 'compact'
}

function ConsecutivosPopoverBody ({
  fs,
  nc,
  dcto,
  headingId
}: {
  fs: string
  nc: string
  dcto: string[]
  headingId: string
}) {
  return (
    <>
      <div className="mb-2 flex items-center gap-2 border-b border-slate-100 pb-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-800">
          <DocumentTextIcon className="h-4 w-4" aria-hidden />
        </span>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            OFIMA
          </p>
          <p id={headingId} className="text-sm font-medium text-slate-800">
            Factura, nota crédito y abonos
          </p>
        </div>
      </div>
      <dl className="m-0 space-y-2">
        {fs ? (
          <div className="rounded-lg bg-slate-50 px-2.5 py-2">
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              FS — Factura
            </dt>
            <dd className="mt-0.5 break-all font-mono text-sm font-medium text-slate-900">
              {fs}
            </dd>
          </div>
        ) : null}
        {nc ? (
          <div className="rounded-lg bg-slate-50 px-2.5 py-2">
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              NC — Nota crédito
            </dt>
            <dd className="mt-0.5 break-all font-mono text-sm font-medium text-slate-900">
              {nc}
            </dd>
          </div>
        ) : null}
        {dcto.length > 0 ? (
          <div className="rounded-lg bg-slate-50 px-2.5 py-2">
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              DCTO — Abonos
            </dt>
            <dd className="mt-1 flex flex-wrap gap-1.5">
              {dcto.map((abonoId) => (
                <span
                  key={abonoId}
                  className="inline-flex items-center rounded-md bg-white px-2 py-0.5 font-mono text-xs font-medium text-slate-900 ring-1 ring-slate-200"
                >
                  {abonoId}
                </span>
              ))}
            </dd>
          </div>
        ) : null}
      </dl>
    </>
  )
}

/**
 * Remisión OFIMA con panel para FS / NC cuando el API envía `ofima_consecutivos`.
 */
const OfimaRemisionWithConsecutivos: React.FC<OfimaRemisionWithConsecutivosProps> = ({
  idRemision,
  ofimaConsecutivos,
  className = '',
  variant = 'default'
}) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const headingId = `${panelId}-title`

  const normalize = (v: unknown): string => {
    if (v == null) return ''
    return String(v).trim()
  }
  const normalizeArray = (v: unknown): string[] => {
    if (Array.isArray(v)) {
      return v
        .map((item) => normalize(item))
        .filter((item) => item.length > 0)
    }
    const single = normalize(v)
    return single ? [single] : []
  }
  const fs = normalize(ofimaConsecutivos?.fs)
  const nc = normalize(ofimaConsecutivos?.nc)
  const dcto = normalizeArray(ofimaConsecutivos?.dcto)
  const hasExtras = fs.length > 0 || nc.length > 0 || dcto.length > 0

  useEffect(() => {
    if (!hasExtras) setOpen(false)
  }, [hasExtras])

  const remisionText =
    idRemision != null && String(idRemision).trim() !== ''
      ? String(idRemision).trim()
      : '—'

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const popoverShell =
    'absolute right-0 top-full z-50 mt-1.5 w-[min(18rem,calc(100vw-1.5rem))] origin-top-right rounded-xl border border-slate-200/80 bg-white p-3 text-left shadow-xl shadow-slate-200/50 ring-1 ring-slate-900/5'

  const triggerCompact =
    'group inline-flex h-7 shrink-0 items-center gap-0.5 rounded-full border border-emerald-200/90 bg-emerald-50/90 px-1.5 text-emerald-800 transition hover:border-emerald-300 hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-1'

  const triggerDefault =
    'group inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1'

  const renderTrigger = (compact: boolean) => (
    <button
      type="button"
      className={compact ? triggerCompact : triggerDefault}
      onClick={(e) => {
        e.stopPropagation()
        setOpen((v) => !v)
      }}
      aria-expanded={open}
      aria-controls={panelId}
      aria-haspopup="dialog"
      aria-label={
        open
          ? 'Cerrar detalle de consecutivos FS, NC y DCTO'
          : 'Ver consecutivos FS, NC y DCTO de OFIMA'
      }
    >
      <span className={compact ? 'sr-only' : 'text-xs font-medium text-slate-600'}>
        {compact ? '' : 'FS / NC'}
      </span>
      <ChevronDownIcon
        className={
          compact
            ? `h-4 w-4 text-emerald-700 transition-transform duration-200 group-hover:text-emerald-900 ${open ? 'rotate-180' : ''}`
            : `h-4 w-4 text-slate-500 transition-transform duration-200 group-hover:text-slate-700 ${open ? 'rotate-180' : ''}`
        }
        aria-hidden
      />
    </button>
  )

  if (variant === 'compact') {
    return (
      <div
        ref={wrapRef}
        className={`relative inline-flex max-w-full items-center gap-1 ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        <span
          className="max-w-[5.5rem] truncate text-sm font-semibold text-emerald-800 tabular-nums"
          title={remisionText}
        >
          {remisionText}
        </span>
        {hasExtras ? renderTrigger(true) : null}
        {open && hasExtras ? (
          <div
            id={panelId}
            role="dialog"
            aria-modal="false"
            aria-labelledby={headingId}
            className={popoverShell}
          >
            <ConsecutivosPopoverBody fs={fs} nc={nc} dcto={dcto} headingId={headingId} />
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div ref={wrapRef} className={`relative inline-flex flex-wrap items-center gap-2 ${className}`}>
      <h4 className="m-0 flex flex-wrap items-center gap-2 text-base">
        <span className="text-slate-600"># Remisión OFIMA</span>
        <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-sm font-semibold text-slate-900 tabular-nums">
          {remisionText}
        </span>
        {hasExtras ? renderTrigger(false) : null}
      </h4>
      {open && hasExtras ? (
        <div
          id={panelId}
          role="dialog"
          aria-modal="false"
          aria-labelledby={headingId}
          className={popoverShell}
        >
          <ConsecutivosPopoverBody fs={fs} nc={nc} dcto={dcto} headingId={headingId} />
        </div>
      ) : null}
    </div>
  )
}

export default OfimaRemisionWithConsecutivos
